import { useState, useCallback, useMemo } from "react";

/**
 * useEmailFilterState — Checkpoint 3.4E.
 *
 * Owns the single, shared draft/applied query-filter state for the Email
 * tab's unified filter bar (Overview and Performance both consume this —
 * see EmailDashboardPanel.jsx). Email view selection is NOT part of this
 * state: the view is immediate navigation, not an Apply-controlled query
 * filter (see EmailFilterBar.jsx), so it stays owned separately by
 * EmailDashboardPanel exactly as before.
 *
 * Draft vs. applied is a real state split, not a visual simulation:
 * every setter here mutates `draft` only. Nothing here ever mutates
 * `applied` except `apply()` itself, which atomically copies the entire
 * draft object in one state update — so a consumer of `applied` (a data
 * hook via useEffect on its values) re-fetches exactly once per Apply
 * click, never once per dropdown change.
 *
 * Dependent-filter semantics (Program -> Campaign -> Broadcast) are
 * ported unchanged from the pre-3.4E cascading logic that used to live in
 * EmailDashboardPanel.jsx directly: changing Program always resets
 * Campaign+Broadcast; changing Campaign always resets Broadcast. This
 * happens on the DRAFT only — an applied selection is never silently
 * altered out from under an already-rendered view; the user must Apply
 * the new (now-cascaded) draft explicitly.
 */

export const EMAIL_FILTER_DEFAULTS = Object.freeze({
  // EMAIL-DASHBOARD-PERIOD-003: default changed from "mtd" ("This Month")
  // to "today" ("Today") -- this is the SINGLE authoritative default for
  // the Email Dashboard's Period filter. Both the initial `draft`/`applied`
  // useState seeds below AND reset()/resetFields() all read this same
  // constant directly, so this one-line change is the complete fix: no
  // second default exists anywhere to also update. Every other Period
  // option, its order, Custom, and every other governed filter's own
  // default are unaffected.
  period: "today",
  customRange: null, // { startDate: "YYYY-MM-DD", endDate: "YYYY-MM-DD" } | null — only meaningful when period === "custom"
  environment: "all",
  program: "all",
  campaign: "all",
  broadcast: "all",
  // DASH-EMAIL-004: Audience's four contextual filter dimensions. Additive
  // only — every other view's draft/applied shape and behavior above is
  // unchanged; these four fields simply ride along on the same shared
  // draft/applied objects, exactly like program/campaign/broadcast already
  // do for views that don't render them.
  leadSource: "all",
  acquisitionChannel: "all",
  geography: "all",
  lifecycleState: "all",
  // DASH-EMAIL-011a: Creative Analytics' one contextual filter dimension
  // (Angle — DASH-EMAIL-011 §14). Additive only, same rule as the
  // Audience block above. Retires DASH-EMAIL-005b's `templateVariant` —
  // that field had no governed equivalent (EMAIL-ANALYTICS-SPEC-001 §20
  // explicitly rejects the flat, un-versioned "template variant" concept
  // it belonged to) and is removed along with the surface that used it.
  angle: "all",
  // DASH-EMAIL-P1-IMPL-006: Email Overview's two real, governed EDT
  // filters that do NOT share a value space with the mock-catalog-driven
  // `broadcast` above -- `broadcast` stays exclusively Performance's own
  // synthetic "brd-*" catalog (untouched); a real BRC#### id in that same
  // field would silently match nothing there. Additive only, same rule as
  // every other block above -- these two fields simply ride along on the
  // shared draft/applied objects for every view that doesn't render them
  // (identical to how `broadcast` itself already rides along for views
  // that don't show it). See EmailFilterBar.jsx's own
  // EMAIL_FILTER_BAR_FIELDS.overview for where these actually render.
  governedBroadcastId: "all",
  governedState: "all",
  // DASH-EMAIL-P1-IMPL-032: three more real, governed filters -- same
  // rule as governedBroadcastId/governedState above (independent of one
  // another and of the mock Program -> Campaign -> Broadcast cascade;
  // additive, ride along on the shared draft/applied objects for every
  // view that doesn't render them). Values are always the stable
  // governed operational ID (AUD###/PRG###/CMP####), never a display
  // name -- see EmailFilterBar.jsx's own governed-selector comments.
  governedAudienceId: "all",
  governedProgramId: "all",
  governedCampaignId: "all",
});

function customRangeEqual(a, b) {
  const aStart = a?.startDate ?? null;
  const aEnd = a?.endDate ?? null;
  const bStart = b?.startDate ?? null;
  const bEnd = b?.endDate ?? null;
  return aStart === bStart && aEnd === bEnd;
}

// A filter set's customRange is only meaningful when period === "custom" —
// see effectiveCustomRange's own comment for why this indirection exists.
function effectiveCustomRange(filters) {
  return filters.period === "custom" ? filters.customRange : null;
}

// Checkpoint 3.5A remediation: generalized, field-scoped equality — an
// adversarial review found that a view rendering only a SUBSET of fields
// (e.g. Deliverability's environment-only EmailFilterBar config) must not
// have its dirty/defaults/Apply semantics driven by fields it doesn't even
// show. `filtersEqual` becomes a thin wrapper over this (ALL_FIELDS),
// so its behavior — and every existing caller's — is unchanged; only the
// internal implementation is now shared with the new field-scoped API
// (isDirtyFor/isDraftAtDefaultsFor/applyFields/resetFields, below).
const ALL_FIELDS = Object.freeze({ period: true, environment: true, program: true, campaign: true, broadcast: true });

function filtersEqualFor(a, b, fields) {
  if (fields.period && a.period !== b.period) return false;
  if (fields.environment && a.environment !== b.environment) return false;
  if (fields.program && a.program !== b.program) return false;
  if (fields.campaign && a.campaign !== b.campaign) return false;
  if (fields.broadcast && a.broadcast !== b.broadcast) return false;
  // DASH-EMAIL-004: additive — a `fields` config that doesn't declare
  // these true (every existing config: full/environmentOnly) simply never
  // reaches these branches, so filtersEqualFor(a, b, ALL_FIELDS) and every
  // pre-existing caller's result is byte-for-byte unchanged.
  if (fields.leadSource && a.leadSource !== b.leadSource) return false;
  if (fields.acquisitionChannel && a.acquisitionChannel !== b.acquisitionChannel) return false;
  if (fields.geography && a.geography !== b.geography) return false;
  if (fields.lifecycleState && a.lifecycleState !== b.lifecycleState) return false;
  // DASH-EMAIL-011a: additive, same reasoning as leadSource/etc above.
  if (fields.angle && a.angle !== b.angle) return false;
  // DASH-EMAIL-P1-IMPL-006: additive, same reasoning -- Email Overview's
  // real governed Broadcast/State filters.
  if (fields.governedBroadcastId && a.governedBroadcastId !== b.governedBroadcastId) return false;
  if (fields.governedState && a.governedState !== b.governedState) return false;
  // DASH-EMAIL-P1-IMPL-032: additive, same reasoning -- Audience/Program/Campaign.
  if (fields.governedAudienceId && a.governedAudienceId !== b.governedAudienceId) return false;
  if (fields.governedProgramId && a.governedProgramId !== b.governedProgramId) return false;
  if (fields.governedCampaignId && a.governedCampaignId !== b.governedCampaignId) return false;
  if (fields.period && !customRangeEqual(effectiveCustomRange(a), effectiveCustomRange(b))) return false;
  return true;
}

function filtersEqual(a, b) {
  return filtersEqualFor(a, b, ALL_FIELDS);
}

// Validity is a query-contract concern, not a UI concern: an applied
// filter set must always be complete enough to issue a real query.
// Every named period is always valid on its own. "custom" additionally
// requires both bounds present and start <= end (ISO yyyy-mm-dd strings
// compare correctly with plain string comparison).
export function isEmailFilterSetValid(filters) {
  if (filters.period !== "custom") return true;
  const startDate = filters.customRange?.startDate;
  const endDate = filters.customRange?.endDate;
  if (!startDate || !endDate) return false;
  return startDate <= endDate;
}

export function useEmailFilterState() {
  const [draft, setDraft] = useState(EMAIL_FILTER_DEFAULTS);
  const [applied, setApplied] = useState(EMAIL_FILTER_DEFAULTS);

  // Checkpoint 3.4E remediation: switching away from "custom" no longer
  // clears the stored draft.customRange — it is left in place, untouched,
  // so switching draft.period custom -> preset -> custom again restores
  // the operator's previously-entered dates instead of presenting
  // unexplained empty fields (the adversarial review's finding). This is
  // safe without any new "history" state because the query-contract
  // hazard the old clearing behavior was guarding against — a stale
  // {startDate, endDate} accidentally constraining a preset-period query
  // — is now prevented at the READ boundary instead of the WRITE
  // boundary: effectiveCustomRange() (used by filtersEqual/apply below)
  // always treats customRange as null whenever period !== "custom",
  // regardless of what raw value happens to still be sitting in state.
  // Nothing reads draft.customRange directly without going through that
  // gate, so a stale value can never leak into a query.
  const setPeriod = useCallback((value) => {
    setDraft((prev) => ({ ...prev, period: value }));
  }, []);

  const setCustomRange = useCallback((range) => {
    setDraft((prev) => ({ ...prev, customRange: range }));
  }, []);

  const setEnvironment = useCallback((value) => {
    setDraft((prev) => ({ ...prev, environment: value }));
  }, []);

  const setProgram = useCallback((value) => {
    setDraft((prev) => ({ ...prev, program: value, campaign: "all", broadcast: "all" }));
  }, []);

  const setCampaign = useCallback((value) => {
    setDraft((prev) => ({ ...prev, campaign: value, broadcast: "all" }));
  }, []);

  const setBroadcast = useCallback((value) => {
    setDraft((prev) => ({ ...prev, broadcast: value }));
  }, []);

  // DASH-EMAIL-004: Audience's four contextual dimensions. Independent of
  // one another (no cascading reset, unlike Program -> Campaign ->
  // Broadcast) — each is its own standalone segment filter.
  const setLeadSource = useCallback((value) => {
    setDraft((prev) => ({ ...prev, leadSource: value }));
  }, []);

  const setAcquisitionChannel = useCallback((value) => {
    setDraft((prev) => ({ ...prev, acquisitionChannel: value }));
  }, []);

  const setGeography = useCallback((value) => {
    setDraft((prev) => ({ ...prev, geography: value }));
  }, []);

  const setLifecycleState = useCallback((value) => {
    setDraft((prev) => ({ ...prev, lifecycleState: value }));
  }, []);

  // DASH-EMAIL-011a: Creative Analytics' one contextual dimension.
  const setAngle = useCallback((value) => {
    setDraft((prev) => ({ ...prev, angle: value }));
  }, []);

  // DASH-EMAIL-P1-IMPL-006: Email Overview's real governed Broadcast/State
  // filters -- independent of one another and of the mock Program ->
  // Campaign -> Broadcast cascade above (no reset side effects).
  const setGovernedBroadcastId = useCallback((value) => {
    setDraft((prev) => ({ ...prev, governedBroadcastId: value }));
  }, []);

  const setGovernedState = useCallback((value) => {
    setDraft((prev) => ({ ...prev, governedState: value }));
  }, []);

  // DASH-EMAIL-P1-IMPL-032: Audience/Program/Campaign's real, governed
  // filters -- independent of one another and of every other filter
  // above (no cascading reset side effects), mirroring
  // governedBroadcastId/governedState exactly.
  const setGovernedAudienceId = useCallback((value) => {
    setDraft((prev) => ({ ...prev, governedAudienceId: value }));
  }, []);

  const setGovernedProgramId = useCallback((value) => {
    setDraft((prev) => ({ ...prev, governedProgramId: value }));
  }, []);

  const setGovernedCampaignId = useCallback((value) => {
    setDraft((prev) => ({ ...prev, governedCampaignId: value }));
  }, []);

  // Restores the DRAFT controls to their defaults only. Applied state
  // (and therefore whatever data is currently on screen) is untouched
  // until the user explicitly hits Apply — Reset must never itself
  // trigger the expensive query.
  const reset = useCallback(() => {
    setDraft(EMAIL_FILTER_DEFAULTS);
  }, []);

  // Checkpoint 3.5A remediation: reset() above replaces the WHOLE draft
  // object — correct for a full-field view, wrong for a narrower one (it
  // would silently wipe a hidden, unrelated draft edit left over from
  // another view, e.g. Deliverability's Reset button clearing a pending
  // Overview Program selection the user never touched on this page).
  // resetFields only overwrites the fields this view's `fields` config
  // marks true, back to their default; every other field is left exactly
  // as-is. For fields === ALL_FIELDS this produces the same draft object
  // reset() would.
  const resetFields = useCallback((fields) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (fields.period) {
        next.period = EMAIL_FILTER_DEFAULTS.period;
        next.customRange = EMAIL_FILTER_DEFAULTS.customRange;
      }
      if (fields.environment) next.environment = EMAIL_FILTER_DEFAULTS.environment;
      if (fields.program) next.program = EMAIL_FILTER_DEFAULTS.program;
      if (fields.campaign) next.campaign = EMAIL_FILTER_DEFAULTS.campaign;
      if (fields.broadcast) next.broadcast = EMAIL_FILTER_DEFAULTS.broadcast;
      if (fields.leadSource) next.leadSource = EMAIL_FILTER_DEFAULTS.leadSource;
      if (fields.acquisitionChannel) next.acquisitionChannel = EMAIL_FILTER_DEFAULTS.acquisitionChannel;
      if (fields.geography) next.geography = EMAIL_FILTER_DEFAULTS.geography;
      if (fields.lifecycleState) next.lifecycleState = EMAIL_FILTER_DEFAULTS.lifecycleState;
      if (fields.angle) next.angle = EMAIL_FILTER_DEFAULTS.angle;
      if (fields.governedBroadcastId) next.governedBroadcastId = EMAIL_FILTER_DEFAULTS.governedBroadcastId;
      if (fields.governedState) next.governedState = EMAIL_FILTER_DEFAULTS.governedState;
      if (fields.governedAudienceId) next.governedAudienceId = EMAIL_FILTER_DEFAULTS.governedAudienceId;
      if (fields.governedProgramId) next.governedProgramId = EMAIL_FILTER_DEFAULTS.governedProgramId;
      if (fields.governedCampaignId) next.governedCampaignId = EMAIL_FILTER_DEFAULTS.governedCampaignId;
      return next;
    });
  }, []);

  const isValid = useMemo(() => isEmailFilterSetValid(draft), [draft]);
  const isDirty = useMemo(() => !filtersEqual(draft, applied), [draft, applied]);

  // Checkpoint 3.4F: a second, deliberately DISTINCT equality check from
  // isDirty. isDirty compares draft against applied — it goes false the
  // instant Apply is clicked, since draft and applied are now identical.
  // Reset's visibility must NOT do that: the required behavior is that
  // Reset stays visible after Apply whenever the (now-applied) filters are
  // still non-default, since Reset-then-Apply is still the only way back
  // to the defaults at that point. So this compares draft against the
  // fixed EMAIL_FILTER_DEFAULTS constant instead of the moving `applied`
  // target, reusing the same semantic (not reference) equality — including
  // the effectiveCustomRange gate — that filtersEqual already uses.
  const isDraftAtDefaults = useMemo(() => filtersEqual(draft, EMAIL_FILTER_DEFAULTS), [draft]);

  // Checkpoint 3.5A remediation: field-scoped dirty/defaults/validity —
  // ADDITIVE alongside isDirty/isDraftAtDefaults/isValid above, which
  // remain byte-for-byte unchanged and are exactly what a "full" fields
  // config (Overview/Performance) is mathematically equivalent to
  // (filtersEqualFor(a, b, ALL_FIELDS) === filtersEqual(a, b) by
  // construction). A narrower fields config (Deliverability's
  // environment-only) gets a genuinely different, correctly-scoped
  // answer: whether draft/applied/defaults agree ONLY on the fields that
  // view actually renders, ignoring any hidden, unrelated field left
  // non-default by another view sharing this same filterState instance.
  const isDirtyFor = useCallback((fields) => !filtersEqualFor(draft, applied, fields), [draft, applied]);
  const isDraftAtDefaultsFor = useCallback(
    (fields) => filtersEqualFor(draft, EMAIL_FILTER_DEFAULTS, fields),
    [draft]
  );
  // Period/customRange validity is irrelevant to a view that doesn't
  // render Period at all — such a view's Apply must never be blocked by
  // an incomplete/invalid Custom range left over from another view.
  const isValidForFields = useCallback((fields) => (fields.period ? isEmailFilterSetValid(draft) : true), [draft]);

  // Atomic: one state update promotes the entire draft object to
  // applied in a single React commit, so every value (period,
  // environment, program, campaign, broadcast, customRange) changes
  // together — a data hook watching `applied`'s fields fires its refetch
  // effect exactly once, not once per field. Guarded defensively even
  // though the UI is expected to disable the Apply control itself
  // whenever !isValid || !isDirty.
  //
  // customRange is normalized here (null unless period === "custom")
  // before being promoted to `applied` — belt-and-suspenders alongside
  // the same normalization both mock adapters already do independently;
  // `applied` is what's actually threaded to the data hooks, so it should
  // never carry a customRange that doesn't apply to its own period.
  const apply = useCallback(() => {
    if (!isValid || !isDirty) return;
    setApplied({ ...draft, customRange: effectiveCustomRange(draft) });
  }, [draft, isValid, isDirty]);

  // Checkpoint 3.5A remediation: `apply()` above is a full-object replace
  // — correct for a view that renders every field (Overview/Performance),
  // wrong for a narrower view. This is the adversarial-review finding:
  // clicking Apply on Deliverability was silently promoting a hidden,
  // unrelated, non-default Program draft (left over from Overview) into
  // `applied`, even though Deliverability never showed Program at all.
  // applyFields performs a SELECTIVE merge — only the fields this view's
  // `fields` config marks true are copied from draft into applied; every
  // other field is copied from the PREVIOUS applied value, verbatim,
  // regardless of what draft currently holds for it. For fields ===
  // ALL_FIELDS this reduces to exactly the same object apply() would
  // produce, field for field.
  const applyFields = useCallback(
    (fields) => {
      if (!isValidForFields(fields) || !isDirtyFor(fields)) return;
      setApplied((prev) => {
        const next = { ...prev };
        if (fields.period) {
          next.period = draft.period;
          next.customRange = effectiveCustomRange(draft);
        }
        if (fields.environment) next.environment = draft.environment;
        if (fields.program) next.program = draft.program;
        if (fields.campaign) next.campaign = draft.campaign;
        if (fields.broadcast) next.broadcast = draft.broadcast;
        if (fields.leadSource) next.leadSource = draft.leadSource;
        if (fields.acquisitionChannel) next.acquisitionChannel = draft.acquisitionChannel;
        if (fields.geography) next.geography = draft.geography;
        if (fields.lifecycleState) next.lifecycleState = draft.lifecycleState;
        if (fields.angle) next.angle = draft.angle;
        if (fields.governedBroadcastId) next.governedBroadcastId = draft.governedBroadcastId;
        if (fields.governedState) next.governedState = draft.governedState;
        if (fields.governedAudienceId) next.governedAudienceId = draft.governedAudienceId;
        if (fields.governedProgramId) next.governedProgramId = draft.governedProgramId;
        if (fields.governedCampaignId) next.governedCampaignId = draft.governedCampaignId;
        return next;
      });
    },
    [draft, isValidForFields, isDirtyFor]
  );

  // "Hidden" refers specifically to the controls the collapsed primary
  // row does not surface (Program, Campaign, and -- DASH-EMAIL-P1-IMPL-006
  // -- Email Overview's governedState, which also only ever renders in
  // the Advanced row). Broadcast/governedBroadcastId are always visible
  // in the primary row, so both are deliberately excluded from this
  // signal.
  //
  // Checkpoint 3.4E remediation (adversarial-review finding): checking
  // DRAFT alone under-reported after Reset-without-Apply — e.g. Program X
  // applied (currently driving what's on screen), then Reset (draft goes
  // back to "all", but applied.program is still X since Reset never
  // touches applied) — draft-only would say "nothing hidden" while the
  // VISIBLE data was still scoped by the hidden Program filter. The
  // signal now means "Advanced filters are relevant to either the
  // currently displayed query or the pending draft query" — an OR of
  // both, not a second state variable: both `draft` and `applied` already
  // exist on this same hook, so this is a derived read of state that was
  // already there.
  const hasHiddenActiveFilters =
    draft.program !== "all" ||
    draft.campaign !== "all" ||
    draft.governedState !== "all" ||
    draft.governedAudienceId !== "all" ||
    draft.governedProgramId !== "all" ||
    draft.governedCampaignId !== "all" ||
    applied.program !== "all" ||
    applied.campaign !== "all" ||
    applied.governedState !== "all" ||
    applied.governedAudienceId !== "all" ||
    applied.governedProgramId !== "all" ||
    applied.governedCampaignId !== "all";

  return {
    draft,
    applied,
    isValid,
    isDirty,
    isDraftAtDefaults,
    hasHiddenActiveFilters,
    setPeriod,
    setCustomRange,
    setEnvironment,
    setProgram,
    setCampaign,
    setBroadcast,
    setLeadSource,
    setAcquisitionChannel,
    setGeography,
    setLifecycleState,
    setAngle,
    setGovernedBroadcastId,
    setGovernedState,
    setGovernedAudienceId,
    setGovernedProgramId,
    setGovernedCampaignId,
    reset,
    apply,
    // Checkpoint 3.5A remediation: field-scoped variants — see each
    // function's own comment above for why these are additive, not
    // replacements.
    isDirtyFor,
    isDraftAtDefaultsFor,
    isValidForFields,
    applyFields,
    resetFields,
  };
}

export default useEmailFilterState;
